import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { authService } from '../lib/auth/auth-service';
import { SessionData, UserData, getUserData, clearSession } from '../lib/session/session-storage';
import { LoadingScreen } from '../components/LoadingScreen';

interface AuthContextType {
  session: SessionData | null;
  user: UserData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<SessionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const initializedRef = useRef(false); // Prevent duplicate initialization in StrictMode

  useEffect(() => {
    if (initializedRef.current) {
      console.log('[AuthContext] Already initialized, skipping...');
      return;
    }
    initializedRef.current = true;
    initializeAuth();
  }, []);

  async function initializeAuth() {
    console.log('[AuthContext] Initializing authentication...');
    setIsLoading(true);

    try {
      // Always check session with server on mount
      // The API uses cookie-based auth, so this validates the session cookie
      const serverSession = await authService.fetchSession();
      
      if (serverSession && serverSession.authenticated) {
        const userName = `${serverSession.session.persona.first_name} ${serverSession.session.persona.last_name}`.trim();
        console.log('[AuthContext] Session valid, user authenticated:', userName);
        setSession(serverSession);
      } else {
        console.log('[AuthContext] No valid session found');
        clearSession();
        setSession(null);
      }
    } catch (error) {
      console.error('[AuthContext] Failed to validate session:', error);
      clearSession();
      setSession(null);
    } finally {
      setIsLoading(false);
      console.log('[AuthContext] Authentication initialization complete');
    }
  }

  async function login() {
    const { startOAuthFlow } = await import('../lib/auth/oauth');
    await startOAuthFlow();
  }

  async function logout() {
    setIsLoading(true);
    try {
      await authService.logout();
      setSession(null);
      window.location.href = '/signin';
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoading(false);
    }
  }

  async function refreshSession() {
    console.log('[AuthContext] Refreshing session...');
    try {
      const serverSession = await authService.fetchSession();
      if (serverSession && serverSession.authenticated) {
        console.log('[AuthContext] Session refreshed successfully');
        setSession(serverSession);
      } else {
        console.log('[AuthContext] Session refresh failed - no valid session');
        clearSession();
        setSession(null);
      }
    } catch (error) {
      console.error('[AuthContext] Failed to refresh session:', error);
      clearSession();
      setSession(null);
    }
  }

  const value: AuthContextType = {
    session,
    user: getUserData(),
    isAuthenticated: !!session && session.authenticated,
    isLoading,
    login,
    logout,
    refreshSession,
  };

  if (isLoading) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
